import type { ProductSlugs } from '@deque/billing-utils';
import { useCallback } from 'react';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useFeatureFlags } from '~/common/contexts/featureFlags';
import { useProducts } from '~/common/contexts/products';
import {
  type EnterpriseMembers,
  memberHasAccepted,
  type ProductAccess
} from '~/common/utils/billing-client/client-v2';
import { formatLongDateTime } from '~/common/utils/dates';
import { getHiddenProductSlugsFromSubs } from '../utils/get-hidden-product-slugs-from-subs';

const useCSVExport = () => {
  const { getProductBySlug } = useProducts();
  const { activeEnterprise } = useEnterprises();
  const { featureFlags } = useFeatureFlags();
  const hiddenProductSlugs = getHiddenProductSlugsFromSubs(
    activeEnterprise?.subscriptions || [],
    getProductBySlug,
    featureFlags
  );

  const processData = useCallback(
    (
      data: EnterpriseMembers.PendingOrAcceptedMember[],
      headers: string[] = []
    ) => {
      if (!data || !data.length) {
        return '';
      }

      // If headers aren't provided, use object keys from first data item
      const csvHeaders = headers.length > 0 ? headers : Object.keys(data[0]);

      // Create CSV header row
      const headerRow = csvHeaders.join(',');

      // Create data rows
      const csvRows = data.map(row => {
        return csvHeaders
          .map((header: string) => {
            let field: string;

            if (header === 'product_access') {
              const productAccess = row.product_access
                .map((product: ProductAccess) => {
                  if (
                    hiddenProductSlugs.includes(
                      product.product_slug as ProductSlugs
                    )
                  ) {
                    return '';
                  }

                  return `${
                    getProductBySlug(product.product_slug)?.name
                  } (${product.access_type.toLowerCase()})`;
                })
                .filter(access => access !== '');

              productAccess.unshift(
                `Axe Account (${row.is_admin ? 'admin' : 'general'})`
              );

              field = productAccess.join(' | ');
            } else if (header === 'status') {
              field = 'user_id' in row ? 'Active' : 'Email sent';
            } else if (header === 'last_activity') {
              if (!memberHasAccepted(row)) {
                field = 'Invite pending';
              } else if (row.user_activity.length === 0) {
                field = 'No activity';
              } else {
                const dates = row.user_activity.map(a => a.updated_at);
                dates.sort();
                field = formatLongDateTime(new Date(dates[dates.length - 1]));
              }
            } else {
              // Dynamic lookup by a runtime header string has no index
              // signature on the member type, so bridge through unknown for
              // this access only.
              field =
                (row as unknown as Record<string, unknown>)[
                  header
                ]?.toString() ?? '';
            }

            // Escape fields that contain commas, quotes, or newlines
            if (
              field.includes(',') ||
              field.includes('"') ||
              field.includes('\n')
            ) {
              field = `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          })
          .join(',');
      });

      // Combine headers and data
      return [headerRow, ...csvRows].join('\n');
    },
    []
  );

  return { processData };
};

export default useCSVExport;
